import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Settings2 } from 'lucide-react';
import {
  formatCurrency,
  formatCompactMoney,
  summariseHeadline,
  coverageCells,
  unwrapCount,
  unwrapRows,
  hasPricingValue,
  OPPORTUNITIES_PAGE_SIZE,
  resolveQueueTabs,
  rowMatchesMapTypeFilter,
  indexTenantAccounts,
  resolveAccountRecordIdFromIndex,
  HEADLINE_TOOLTIPS,
  mktLabel,
} from '@/lib/marketExperience';
import { useTranslation } from '@/components/utils/translations';
import { fetchMarketOpportunities, fetchMarketPreferences, fetchMarketSummary, fetchPilotStatus } from '@/lib/marketExperienceApi';
import { countryToQueryParam, lmsToQueryParam } from '@/lib/marketUserFilters';
import { invalidatePilotStatus } from '@/hooks/usePilotStatus';
import { AccountFinal } from '@/api/entities';
import MarketHelpLabel from './MarketHelpLabel';
import MarketCoverageMap from './MarketCoverageMap';
import MarketOpportunitiesTable from './MarketOpportunitiesTable';
import MarketOpportunityDrawer from './MarketOpportunityDrawer';
import MarketChanges from './MarketChanges';
import MarketPreferencesDrawer from './MarketPreferencesDrawer';
import MarketMixGrid from './MarketMixGrid';

function HeadlineStrip({ summary, onSelect }) {
  const { t } = useTranslation();
  const items = useMemo(() => summariseHeadline(summary), [summary]);
  const cells = useMemo(() => coverageCells(summary), [summary]);
  const termYears = summary?.value?.term_years;
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((f) => {
          const money = formatCompactMoney(f.amount, f.currency);
          const annual = formatCompactMoney(f.annual, f.currency);
          const valueLine = f.basis === 'tcv' && annual
            ? (termYears
              ? t('mktValueWholeContractOverYears', { annual, years: termYears })
              : t('mktValueWholeContract', { annual }))
            : (annual ? t('mktAnnualRunRate', { annual }) : null);
          const hint = [mktLabel(t, f.tooltip), valueLine].filter(Boolean).join(' ');
          return (
            <button
              key={f.key}
              type="button"
              className="rounded-lg bg-white shadow-sm px-4 py-3 text-left hover:ring-1 hover:ring-[#00C2C2]"
              onClick={() => onSelect?.(f.key)}
            >
              <p className="text-[10px] uppercase tracking-wide text-[#5C6B7A]">
                <MarketHelpLabel label={mktLabel(t, f.label)} hint={hint} />
              </p>
              <p className="text-2xl font-semibold text-[#0A1A2F] mt-0.5">
                {f.count == null ? '—' : Number(f.count).toLocaleString()}
                {money ? <span className="text-lg font-semibold text-[#5C6B7A]"> · {money}</span> : null}
              </p>
            </button>
          );
        })}
      </div>
      {cells?.examined != null && cells?.examinable != null ? (
        <p className="text-xs text-[#5C6B7A]">
          <MarketHelpLabel
            label={t('mktExaminedOf', {
              examined: Number(cells.examined).toLocaleString(),
              total: Number(cells.examinable).toLocaleString(),
            })}
            hint={mktLabel(t, HEADLINE_TOOLTIPS.examined)}
          />
        </p>
      ) : null}
    </div>
  );
}

function formatQuoted(text) {
  const s = String(text || '');
  const parts = s.split(/("(?:[^"]+)")/g);
  if (parts.length === 1) {
    // A single-quoted span must OPEN a word and CLOSE before a non-letter. The bare /'[^']+'/ paired the
    // apostrophes of two possessives ("Universidade de Lisboa's … Université Laval's") and italicised the
    // text between them as if it were a quotation (wooclap Overview, 2026-11-02).
    const single = s.split(/((?:^|(?<=[\s([{—–-]))'[^'\n]+?'(?![\p{L}\p{N}]))/gu);
    if (single.length === 1) return s;
    return single.map((chunk, i) => (
      chunk.startsWith("'") && chunk.endsWith("'") && chunk.length > 2
        ? <em key={i}>{chunk}</em>
        : chunk
    ));
  }
  return parts.map((chunk, i) => (
    chunk.startsWith('"') && chunk.endsWith('"') && chunk.length > 2
      ? <em key={i}>{chunk}</em>
      : chunk
  ));
}

function NeoBrief({ brief }) {
  if (!brief) return null;
  const headline = typeof brief === 'object' ? brief.headline : (typeof brief === 'string' ? brief : null);
  const points = Array.isArray(brief?.points) ? brief.points : [];
  const items = points.map((p) => {
    if (typeof p === 'string') return { label: null, body: p.trim() };
    return { label: p.label || null, body: (p.body || p.text || '').trim() };
  }).filter((p) => p.label || p.body);
  const banned = /most rows are candidates/i;
  const safeItems = items.filter((p) => !banned.test(`${p.label || ''} ${p.body || ''}`));
  const safeHeadline = headline && !banned.test(headline) ? headline : (banned.test(headline || '') ? null : headline);
  if (!safeHeadline && !safeItems.length) return null;
  return (
    <div className="w-full space-y-2 text-sm text-[#0A1A2F] leading-relaxed">
      {safeHeadline ? <p>{formatQuoted(safeHeadline)}</p> : null}
      {safeItems.map((p, i) => (
        <p key={i}>
          {p.label ? <span className="font-semibold">{p.label}</span> : null}
          {p.label && p.body ? ' — ' : null}
          {p.body ? formatQuoted(p.body) : null}
        </p>
      ))}
    </div>
  );
}

export default function MarketExperience({ tenantId, lens, lenses = [], category = 'all', canEdit = false, countryFilter = 'all', lmsVendors = [], lmsFamilies = [], onLmsVendorsChange, filtersReady = true, onCountryChange, moduleStatus, onOpenPreferences }) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [opps, setOpps] = useState([]);
  const [oppsTotal, setOppsTotal] = useState(0);
  const [oppsPayload, setOppsPayload] = useState(null);
  const [oppsLoading, setOppsLoading] = useState(true);
  const [oppsError, setOppsError] = useState(null);
  const [queue, setQueue] = useState('priority');
  const [drawerRow, setDrawerRow] = useState(null);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const [rescoreStartedAt, setRescoreStartedAt] = useState(null);
  const [rescoreElapsedMs, setRescoreElapsedMs] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [pilotStatus, setPilotStatus] = useState(null);
  const [pilotRefreshKey, setPilotRefreshKey] = useState(0);

  const markRescoring = useCallback((on) => {
    setRescoring(Boolean(on));
    if (on) {
      setRescoreStartedAt(Date.now());
      setRescoreElapsedMs(0);
    } else {
      setRescoreStartedAt(null);
      setRescoreElapsedMs(0);
    }
  }, []);

  useEffect(() => {
    if (!rescoring || !rescoreStartedAt) return undefined;
    const tick = () => setRescoreElapsedMs(Date.now() - rescoreStartedAt);
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [rescoring, rescoreStartedAt]);

  const countryParam = countryToQueryParam(countryFilter);
  const lmsParam = lmsToQueryParam(lmsVendors);

  const markAccountInAccounts = useCallback((accountId, patch = {}) => {
    const id = String(accountId || '').trim();
    if (!id) return;
    setOpps((prev) => (prev || []).map((r) => (
      String(r.account_id || '') === id ? { ...r, ...patch } : r
    )));
    setDrawerRow((prev) => (
      prev && String(prev.account_id || '') === id ? { ...prev, ...patch } : prev
    ));
  }, []);
  const [preferences, setPreferences] = useState(null);
  const [offset, setOffset] = useState(0);
  const [typeFilter, setTypeFilter] = useState('all');
  /* [2026-10-07] CATEGORY comes from the page header (pages/Market.jsx), not from local state.
   * It scopes the summary AND the table together, which is the point: one control, one story.
   * It was briefly a second dropdown inside the table — two controls for one piece of state is
   * how the funnel and the tabs drift apart, which this page has a long history of. */
  const categoryFilter = category || 'all';
  const [searchExpanded, setSearchExpanded] = useState(false);

  const needsSetup = moduleStatus === 'needs_setup';
  const showPricing = hasPricingValue(summary?.value);

  useEffect(() => {
    setOffset(0);
  }, [countryFilter, lmsVendors, queue, tenantId, lens, typeFilter, categoryFilter, searchExpanded]);


  const handleSearchActiveChange = useCallback((active) => {
    setSearchExpanded((prev) => (prev === active ? prev : active));
  }, []);

  useEffect(() => {
    if (queue === 'adjacent' && typeFilter !== 'all') {
      setTypeFilter('all');
    }
  }, [queue, typeFilter]);

  /* [2026-10-21] The brief is an LLM composition and costs 3.0-4.4s on every cache
   * miss (the server cache is in-process and per-container, so a Cloud Run instance
   * that has not composed this snapshot pays full price). Two consequences had to be
   * handled here, because together they read as "the summary doesn't always appear":
   *
   *   1. There was no loading state for the prose — see the render below.
   *   2. Nothing guarded WHICH request was allowed to write state. Every dep in this
   *      callback (lens, filtersReady, countryParam, categoryFilter) settles
   *      asynchronously on mount, so a superseded request could still land, and a
   *      superseded request that REJECTED ran setSummary(null) — blanking a brief
   *      that had already rendered correctly.
   *
   * A monotonic request id fixes the second: only the newest request may write. */
  const summaryReqId = useRef(0);
  const loadSummary = useCallback(async () => {
    if (!tenantId || needsSetup || !lens || !filtersReady) {
      summaryReqId.current += 1;      // invalidate anything still in flight
      setSummary(null);
      setSummaryLoading(Boolean(tenantId && !needsSetup && (!lens || !filtersReady)));
      return;
    }
    const reqId = (summaryReqId.current += 1);
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const data = await fetchMarketSummary(tenantId, lens, countryParam, { lms: lmsParam, category: categoryFilter });
      if (reqId !== summaryReqId.current) return;   // superseded; do not overwrite
      setSummary(data);
    } catch (e) {
      if (reqId !== summaryReqId.current) return;   // a stale rejection must not blank a good brief
      setSummary(null);
      setSummaryError(e?.message || 'Could not load market summary');
    } finally {
      if (reqId === summaryReqId.current) setSummaryLoading(false);
    }
  }, [tenantId, needsSetup, lens, countryParam, lmsParam, categoryFilter, filtersReady]);

  const loadOpps = useCallback(async () => {
    if (!tenantId || needsSetup || !lens || !filtersReady) {
      setOpps([]);
      setOppsLoading(Boolean(tenantId && !needsSetup && (!lens || !filtersReady)));
      return;
    }
    setOppsLoading(true);
    setOppsError(null);
    try {
      // Type filter is applied client-side across the queue — fetch a full slice when filtering.
      // Adjacent market has no type dropdown; always show the full adjacent list.
      const effectiveTypeFilter = queue === 'adjacent' ? 'all' : typeFilter;
      const clientPage = queue === 'adjacent' || queue === 'rest' || effectiveTypeFilter !== 'all' || searchExpanded;
      let data = await fetchMarketOpportunities(tenantId, {
        lens,
        queue,
        country: countryParam,
        lms: lmsParam,
        /* Server-side, unlike `type`: the category decides which accounts are in the answer at
         * all, so filtering it client-side over a 200-row slice would misreport `total` and
         * silently truncate any category with more than 200 accounts. */
        category: categoryFilter,
        limit: clientPage ? 200 : OPPORTUNITIES_PAGE_SIZE,
        offset: clientPage ? 0 : offset,
      });
      let rows = unwrapRows(data);
      const counted = data?.queue_counts?.[queue];
      const queueMismatch = Boolean(queue) && rows.some((r) => r.queue) && rows.every((r) => r.queue && r.queue !== queue);
      const emptyDespiteCount = Boolean(queue) && rows.length === 0 && Number(counted) > 0;
      if (queueMismatch || emptyDespiteCount) {
        data = await fetchMarketOpportunities(tenantId, {
          lens,
          country: countryParam,
          lms: lmsParam,
          /* The queue-mismatch fallback must keep the category. Without it this path quietly
           * re-widens to the whole lens and the table fills with the categories the user just
           * filtered out — the hardest kind of bug to see, because the table looks populated. */
          category: categoryFilter,
          limit: 200,
          offset: 0,
        });
        rows = unwrapRows(data).filter((r) => r.queue === queue);
      } else if (queue && rows.length && rows.every((r) => r.queue)) {
        rows = rows.filter((r) => r.queue === queue);
      }
      // Stamp the requested queue when the API omits it on sparse rows
      if (queue) {
        rows = rows.map((r) => (r.queue ? r : { ...r, queue }));
      }
      if (queue === 'rest') {
        rows = [...rows].sort((a, b) => (Number(b.market_score) || 0) - (Number(a.market_score) || 0));
      }
      const accounts = await AccountFinal.filter({ tenant_id: tenantId }, '', 2000).catch(() => []);
      const byKey = indexTenantAccounts(accounts, tenantId);
      rows = rows.map((r) => {
        const recordId = r.tenant_account_uuid
          || resolveAccountRecordIdFromIndex(r, byKey, tenantId);
        const acc = recordId ? byKey[recordId] : null;
        const financial = r.financial_capacity
          || r.financial_band
          || r.financial_capacity_band
          || acc?.financial_capacity
          || acc?.financial_band
          || acc?.financial_situation
          || null;
        const ai = r.ai_readiness_score ?? r.ai_readiness ?? acc?.ai_readiness_score ?? acc?.ai_readiness ?? null;
        return {
          ...r,
          account_record_id: recordId || r.account_record_id || null,
          financial_capacity: financial,
          ai_readiness_score: ai,
        };
      });

      if (effectiveTypeFilter !== 'all') {
        rows = rows.filter((r) => rowMatchesMapTypeFilter(r, effectiveTypeFilter));
      }

      setOppsPayload(data);
      if (searchExpanded) {
        setOpps(rows);
        setOppsTotal(rows.length);
      } else if (clientPage || queueMismatch || emptyDespiteCount) {
        const page = rows.slice(offset, offset + OPPORTUNITIES_PAGE_SIZE);
        setOpps(page);
        setOppsTotal(rows.length);
      } else {
        setOpps(rows);
        setOppsTotal(unwrapCount(data, rows));
      }
    } catch (e) {
      setOpps([]);
      setOppsTotal(0);
      setOppsError(e?.message || 'Could not load opportunities');
    } finally {
      setOppsLoading(false);
    }
  }, [tenantId, needsSetup, lens, queue, countryParam, lmsParam, offset, typeFilter, categoryFilter, searchExpanded, filtersReady]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary, reloadKey]);

  useEffect(() => {
    loadOpps();
  }, [loadOpps, reloadKey]);

  useEffect(() => {
    if (!tenantId || needsSetup) {
      setPilotStatus(null);
      return undefined;
    }
    let cancelled = false;
    fetchPilotStatus(tenantId)
      .then((data) => { if (!cancelled) setPilotStatus(data); })
      .catch(() => { if (!cancelled) setPilotStatus({ pilot_mode: false, label: null }); });
    return () => { cancelled = true; };
  }, [tenantId, needsSetup, pilotRefreshKey]);

  useEffect(() => {
    if (!tenantId || needsSetup || !lens) {
      setPreferences(null);
      return;
    }
    let cancelled = false;
    fetchMarketPreferences(tenantId, lens)
      .then((data) => { if (!cancelled) setPreferences(data); })
      .catch(() => { if (!cancelled) setPreferences(null); });
    return () => { cancelled = true; };
  }, [tenantId, needsSetup, lens, reloadKey]);

  useEffect(() => {
    setQueue('priority');
    setOffset(0);
  }, [lens]);

  if (needsSetup) {
    return (
      <div className="space-y-6">
        <Card className="border-0 shadow-md">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-semibold text-[#0A1A2F]">{t('mktSetUpMarket')}</h2>
            <p className="text-sm text-[#5C6B7A] mt-2 max-w-lg mx-auto">
              {canEdit
                ? t('mktSetupBodyEditor')
                : t('mktSetupBodyViewer')}
            </p>
            {canEdit && (
            <Button
              className="mt-4 bg-[#00C2C2] hover:bg-[#00A8A8] text-white"
              onClick={() => {
                setPrefsOpen(true);
                onOpenPreferences?.();
              }}
            >
              {t('mktOpenSetup')}
            </Button>
            )}
          </CardContent>
        </Card>
        <MarketPreferencesDrawer
          tenantId={tenantId}
          open={prefsOpen}
          onOpenChange={setPrefsOpen}
          forceSetup
          canEdit={canEdit}
          lens={lens}
          lenses={lenses}
          onRescoring={markRescoring}
          onSaved={() => {
            setPrefsOpen(false);
            setReloadKey((k) => k + 1);
            window.location.reload();
          }}
          lmsFamilies={lmsFamilies}
          lmsVendors={lmsVendors}
          onLmsVendorsChange={onLmsVendorsChange}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" className="gap-2" onClick={() => setPrefsOpen(true)}>
          <Settings2 className="w-4 h-4" />
          {t('mktPreferences')}
        </Button>
      </div>
      {rescoring ? (
        <div className="rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#0A1A2F] flex items-center gap-2">
          <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-[#14B8A6] border-t-transparent animate-spin" />
          {t('mktRescoringTheMarket')}
          {rescoreElapsedMs > 0 ? (
            <span className="text-[#5C6B7A]">
              · {rescoreElapsedMs < 60000
                ? `${Math.floor(rescoreElapsedMs / 1000)}s`
                : `${Math.floor(rescoreElapsedMs / 60000)}m ${Math.floor((rescoreElapsedMs % 60000) / 1000)}s`}
            </span>
          ) : null}
        </div>
      ) : null}

      {(summaryError || oppsError) && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {(() => {
            const msg = summaryError || oppsError;
            if (msg === 'Could not load market summary') return t('mktCouldNotLoadSummary');
            if (msg === 'Could not load opportunities') return t('mktCouldNotLoadOpportunities');
            return msg;
          })()}
        </div>
      )}

      {summaryLoading ? (
        <div className="h-16 bg-gray-100 rounded-md animate-pulse" />
      ) : (
        <HeadlineStrip
          summary={summary}
          onSelect={(key) => {
            if (key === 'priority') setQueue('priority');
            else if (key === 'in_market') setQueue('in_market');
            else setQueue('priority');
            document.getElementById('market-opportunities')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      )}

      {/* [2026-10-21] The prose had NO loading state while the stat strip above it did,
        * and the Coverage map below renders immediately — so for the 3-4s the brief takes
        * to compose, the page looked complete with the summary simply missing. That is the
        * "it doesn't always appear" report: it was arriving, just silently and late. */}
      {summaryLoading ? (
        <div className="w-full space-y-2" aria-hidden="true">
          <div className="h-4 bg-gray-100 rounded animate-pulse w-11/12" />
          <div className="h-4 bg-gray-100 rounded animate-pulse w-4/5" />
          <div className="h-4 bg-gray-100 rounded animate-pulse w-9/12" />
        </div>
      ) : (
        <NeoBrief brief={summary?.brief} />
      )}

      <MarketCoverageMap
        tenantId={tenantId}
        lens={lens}
        summary={summary}
        preferences={preferences || (summary?.coverage ? { entitlement: { countries: summary.coverage.countries_entitled } } : null)}
        opportunityRows={opps}
        countryCounts={oppsPayload?.by_country}
        onCountrySelect={(country) => onCountryChange?.(country)}
        onAccountClick={(row) => setDrawerRow(row)}
      />

      {!oppsError && (
        <div id="market-opportunities">
        {(() => {
          const tabs = resolveQueueTabs(oppsPayload);
          return (
            <div className="flex flex-wrap gap-2 mb-2">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setQueue(tab.key)}
                  className={`text-xs px-3 py-1.5 rounded-full ${
                    queue === tab.key ? 'bg-[#00C2C2] text-white' : 'bg-white text-[#0A1A2F] shadow-sm'
                  }`}
                >
                  {mktLabel(t, tab.label)}
                  {tab.count != null ? ` · ${tab.count}` : ''}
                </button>
              ))}
            </div>
          );
        })()}
        <MarketOpportunitiesTable
          rows={opps}
          total={oppsTotal}
          loading={oppsLoading}
          offset={offset}
          pageSize={OPPORTUNITIES_PAGE_SIZE}
          onOffsetChange={setOffset}
          onAccountClick={setDrawerRow}
          onAccountAdded={(accountId, patch) => markAccountInAccounts(accountId, patch)}
          onPilotUsed={() => {
            invalidatePilotStatus(tenantId);
            setPilotRefreshKey((k) => k + 1);
          }}
          tenantId={tenantId}
          lens={lens}
          queue={queue}
          typeFilter={typeFilter}
          onTypeFilterChange={queue === 'adjacent' ? undefined : setTypeFilter}
          onSearchActiveChange={handleSearchActiveChange}
          pilotStatus={pilotStatus}
        />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-6">
          <MarketMixGrid
            summary={summary}
            selectedQueue={queue}
            onSelectQueue={(next) => setQueue(next)}
          />
        </div>
        <div className="lg:col-span-6">
          <MarketChanges tenantId={tenantId} lens={lens} countryFilter={countryFilter} />
        </div>
      </div>

      {showPricing && (
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-base text-[#0A1A2F]">{t('value')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-semibold text-[#0A1A2F]">
              {formatCurrency(
                summary.value.total ?? summary.value.estimated_value ?? summary.value.sum,
                summary.value.currency || summary.value.value_currency,
              )}
            </p>
          </CardContent>
        </Card>
      )}

      <MarketOpportunityDrawer
        tenantId={tenantId}
        lens={lens}
        queue={queue}
        accountId={drawerRow?.account_id}
        seedRow={drawerRow}
        open={Boolean(drawerRow)}
        onOpenChange={(v) => { if (!v) setDrawerRow(null); }}
        showPricing={showPricing}
        onAccountAdded={(accountId, patch) => markAccountInAccounts(accountId, patch)}
        onPilotUsed={() => {
          invalidatePilotStatus(tenantId);
          setPilotRefreshKey((k) => k + 1);
        }}
        pilotStatus={pilotStatus}
        onMovedToIgnore={({ accountId: ignoredId }) => {
          const id = String(ignoredId || '');
          if (id) {
            setOpps((prev) => (prev || []).filter((r) => String(r.account_id || '') !== id));
            setOppsTotal((n) => Math.max(0, (Number(n) || 0) - 1));
            setOppsPayload((prev) => {
              if (!prev || typeof prev !== 'object') return prev;
              const counts = { ...(prev.queue_counts || {}) };
              if (queue && counts[queue] != null) {
                counts[queue] = Math.max(0, (Number(counts[queue]) || 0) - 1);
              }
              return { ...prev, queue_counts: counts };
            });
          }
          setDrawerRow(null);
          // Refetch opportunities + summary so Priority and headline stats match the API.
          setReloadKey((k) => k + 1);
        }}
      />
      <MarketPreferencesDrawer
        tenantId={tenantId}
        lens={lens}
        lenses={lenses}
        canEdit={canEdit}
        open={prefsOpen}
        onOpenChange={setPrefsOpen}
        excluded={summary?.excluded}
        held={summary?.held_for_review}
        lmsFamilies={lmsFamilies}
        lmsVendors={lmsVendors}
        onLmsVendorsChange={onLmsVendorsChange}
        onRescoring={markRescoring}
        onSaved={() => setReloadKey((k) => k + 1)}
      />
    </div>
  );
}
